import React, { useState, useEffect } from 'react';
import { PosTagihan, BillCategoryType, AkunKeuangan } from '../../types/index.js';
import { Plus, Edit2, Trash2, Search, Tag, X } from 'lucide-react';
import { useDialog } from '../ui/DialogProvider.js';
import { useDataTable } from '../../hooks/useDataTable.js';
import { Pagination } from '../ui/Pagination.js';
import { SortableHeader } from '../ui/SortableHeader.js';
import { api } from '../../services/api.js';

interface PosTagihanViewProps {
  posTagihanList: PosTagihan[];
  onAddPos: (data: Partial<PosTagihan>) => Promise<void>;
  onUpdatePos?: (id: string, data: Partial<PosTagihan>) => Promise<void>;
}

export const PosTagihanView: React.FC<PosTagihanViewProps> = ({ posTagihanList, onAddPos, onUpdatePos }) => {
  const { showAlert, showConfirm } = useDialog();
  const [showModal, setShowModal] = useState(false);
  const [editingPos, setEditingPos] = useState<PosTagihan | null>(null);

  // Form states matching document pages 6-8
  const [kode, setKode] = useState('');
  const [namaPos, setNamaPos] = useState('');
  const [jenis, setJenis] = useState<BillCategoryType>('Bulanan');
  const [akunPenerimaan, setAkunPenerimaan] = useState('');
  const [akunPiutang, setAkunPiutang] = useState('');
  const [akunPotongan, setAkunPotongan] = useState('');
  const [deskripsi, setDeskripsi] = useState('');
  const [defaultNominal, setDefaultNominal] = useState('');
  
  const [akunList, setAkunList] = useState<AkunKeuangan[]>([]);

  useEffect(() => {
    api.getAkun().then(setAkunList).catch(console.error);
  }, []);

  const {
    searchQuery: search,
    setSearchQuery: setSearch,
    sortBy,
    sortDir,
    handleSort,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    totalRows,
    paginatedData: filteredPos,
  } = useDataTable<PosTagihan>({
    data: posTagihanList,
    searchFields: ['namaPos', 'kode', 'deskripsi', 'jenis', 'akunPenerimaan'],
    initialSortBy: 'kode',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kode || !namaPos) return;

    const payload = {
      kode,
      namaPos,
      jenis,
      akunPenerimaan,
      akunPiutang,
      akunPotongan,
      deskripsi,
      defaultNominal: Number(defaultNominal) || 0,
    };

    if (editingPos && onUpdatePos) {
      await onUpdatePos(editingPos.id, payload);
    } else {
      await onAddPos(payload);
    }

    handleCloseModal();
  };

  const handleEditClick = (pos: PosTagihan) => {
    setEditingPos(pos);
    setKode(pos.kode);
    setNamaPos(pos.namaPos);
    setJenis(pos.jenis);
    setAkunPenerimaan(pos.akunPenerimaan || '');
    setAkunPiutang(pos.akunPiutang || '');
    setAkunPotongan(pos.akunPotongan || '');
    setDeskripsi(pos.deskripsi || '');
    setDefaultNominal(pos.defaultNominal?.toString() || '');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPos(null);
    setKode('');
    setNamaPos('');
    setJenis('Bulanan');
    setDeskripsi('');
    setDefaultNominal('');
    setAkunPenerimaan('');
    setAkunPiutang('');
    setAkunPotongan('');
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Top Header Matching Document Page 6 */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">Pos Tagihan Sekolah</h2>
          <p className="text-xs text-slate-500">
            Kategori biaya sekolah (SPP, Pengembangan, Seragam, MPLS, Tahfidz, Qurban) & pemetaan akun akuntansi
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search pos tagihan..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 w-48"
            />
          </div>

          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold px-3.5 py-1.5 rounded-md transition shadow-xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Tambah</span>
          </button>
        </div>
      </div>

      {/* Main Table Exact from Document Page 6 */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden flex flex-col min-h-[400px]">
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase sticky top-0 z-10">
              <tr>
                <th className="py-2.5 px-3 w-28">Aksi</th>
                <th className="py-2.5 px-3 w-12 text-center">No</th>
                <SortableHeader label="Kode" columnKey="kode" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="w-20" />
                <SortableHeader label="Nama Pos" columnKey="namaPos" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Jenis" columnKey="jenis" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="text-center" />
                <SortableHeader label="Deskripsi" columnKey="deskripsi" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Akun Penerimaan" columnKey="akunPenerimaan" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Akun Piutang" columnKey="akunPiutang" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Akun Potongan" columnKey="akunPotongan" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPos.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-8 text-slate-400">
                    Tidak ada data pos tagihan yang cocok
                  </td>
                </tr>
              ) : (
                filteredPos.map((pos, idx) => (
                  <tr key={pos.id} className="hover:bg-sky-50/50 transition">
                    <td className="py-2 px-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEditClick(pos)}
                        className="px-1.5 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded border border-emerald-300 flex items-center gap-0.5"
                      >
                        <Edit2 className="w-2.5 h-2.5" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={async () => await showConfirm(`Hapus pos ${pos.namaPos}?`, { type: 'danger', title: 'Hapus Pos Tagihan' })}
                        className="px-1.5 py-0.5 text-[10px] font-medium bg-rose-50 text-rose-700 hover:bg-rose-100 rounded border border-rose-300 flex items-center gap-0.5"
                      >
                        <Trash2 className="w-2.5 h-2.5" />
                        <span>Del</span>
                      </button>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-center text-slate-500 font-mono">{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                    <td className="py-2 px-3 font-mono font-bold text-sky-800">{pos.kode}</td>
                    <td className="py-2 px-4 font-semibold text-slate-900">{pos.namaPos}</td>
                    <td className="py-2 px-3 text-center">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          pos.jenis === 'Bulanan'
                            ? 'bg-sky-100 text-sky-800 border border-sky-200'
                            : pos.jenis === 'Tahunan'
                            ? 'bg-amber-100 text-amber-800 border border-amber-200'
                            : 'bg-purple-100 text-purple-800 border border-purple-200'
                        }`}
                      >
                        {pos.jenis}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-slate-600 truncate max-w-[200px]" title={pos.deskripsi}>
                      {pos.deskripsi}
                    </td>
                    <td className="py-2 px-3 text-slate-500 truncate max-w-[150px]" title={pos.akunPenerimaan}>
                      {akunList.find(a => a.id === pos.akunPenerimaan)?.namaAkun || pos.akunPenerimaan || '-'}
                    </td>
                    <td className="py-2 px-3 text-slate-500 truncate max-w-[150px]" title={pos.akunPiutang}>
                      {akunList.find(a => a.id === pos.akunPiutang)?.namaAkun || pos.akunPiutang || '-'}
                    </td>
                    <td className="py-2 px-3 text-slate-500 truncate max-w-[150px]" title={pos.akunPotongan}>
                      {akunList.find(a => a.id === pos.akunPotongan)?.namaAkun || pos.akunPotongan || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          totalRows={totalRows}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      </div>

      {/* Modal Tambah / Edit Pos Tagihan */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full p-6 border border-slate-200 animate-in fade-in">
            <div className="flex items-center justify-between pb-3 border-b mb-4">
              <h3 className="text-sm font-bold text-slate-900">
                {editingPos ? 'Edit Pos Tagihan' : 'Tambah Pos Tagihan'}
              </h3>
              <button onClick={handleCloseModal} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Kode</label>
                  <input
                    type="text"
                    placeholder="125"
                    value={kode}
                    onChange={(e) => setKode(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md font-mono"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-slate-700 font-semibold mb-1">Nama Pos</label>
                  <input
                    type="text"
                    placeholder="Nama Pos Tagihan"
                    value={namaPos}
                    onChange={(e) => setNamaPos(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Jenis Dropdown exact from page 7 */}
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Jenis Tagihan</label>
                  <select
                    value={jenis}
                    onChange={(e) => setJenis(e.target.value as BillCategoryType)}
                    className="w-full px-3 py-2 border rounded-md bg-white font-medium"
                  >
                    <option value="Bulanan">1 - Bulanan</option>
                    <option value="Tahunan">2 - Tahunan</option>
                    <option value="Cicilan">3 - Cicilan</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Default Tarif (Rp)</label>
                  <input
                    type="number"
                    placeholder="200000"
                    value={defaultNominal}
                    onChange={(e) => setDefaultNominal(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Akun Penerimaan</label>
                <select
                  value={akunPenerimaan}
                  onChange={(e) => setAkunPenerimaan(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-white"
                >
                  <option value="">-- Pilih Akun Penerimaan --</option>
                  {akunList.filter(a => a.tipeAkun === 'PENDAPATAN' || a.tipeAkun === 'ASET').map(a => (
                    <option key={a.id} value={a.id}>
                      {a.id} - {a.namaAkun}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Akun Piutang</label>
                <select
                  value={akunPiutang}
                  onChange={(e) => setAkunPiutang(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-white"
                >
                  <option value="">-- Pilih Akun Piutang --</option>
                  {akunList.filter(a => a.tipeAkun === 'ASET').map(a => (
                    <option key={a.id} value={a.id}>
                      {a.id} - {a.namaAkun}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Akun Potongan</label>
                <select
                  value={akunPotongan}
                  onChange={(e) => setAkunPotongan(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-white"
                >
                  <option value="">-- Pilih Akun Potongan --</option>
                  {akunList.filter(a => a.tipeAkun === 'BIAYA' || a.tipeAkun === 'PENDAPATAN').map(a => (
                    <option key={a.id} value={a.id}>
                      {a.id} - {a.namaAkun}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Deskripsi</label>
                <textarea
                  placeholder="Keterangan pos tagihan..."
                  value={deskripsi}
                  onChange={(e) => setDeskripsi(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-md"
                ></textarea>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-1.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md font-medium"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="px-5 py-1.5 bg-rose-600 hover:bg-rose-500 text-white rounded-md font-semibold"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
